import { type NumEntry } from '@/state/market-data-cache'
import {
  BookAction,
  type BookEntry,
  type Instrument,
  type OrderBook,
  UpdateType,
} from '@/types/generated/common/types'

/**
 * Shared, framework-free orderbook helpers used by the orderbook widget and any
 * other widget that needs to read the same `${exchange}:${symbol}`-keyed bid/ask
 * caches (e.g. the Quick Order popup).
 */

/** Trailing-edge flush cadence for buffered orderbook delta updates. */
export const ORDERBOOK_DELTA_FLUSH_INTERVAL_MS = 500

/**
 * Backpressure threshold on buffered-but-unflushed deltas, in entries.
 *
 * This is deliberately NOT a "give up and resync" cap. Buffered deltas are already
 * sequence-validated by processOrderbookBatch, so applying them early is always safe —
 * the only thing the flush interval buys is fewer React renders. When a fast market
 * pushes more than this many entries into one flush window the right answer is to drain
 * the buffer immediately (see the ORDERBOOK_DELTA_BACKPRESSURE_LIMIT call sites), not to
 * throw the book away: the old behaviour blanked the widget precisely when the market
 * was moving, because the threshold scales with update rate.
 */
export const ORDERBOOK_DELTA_BACKPRESSURE_LIMIT = 500

/**
 * How long to wait for a snapshot after a forced resubscribe before trying again, and the
 * ceiling that exponential backoff grows toward. Retries never stop: a book that failed to
 * resync twice used to stay dead until the symbol changed, so recovery now keeps going,
 * just at a decreasing rate that can't thrash the socket.
 *
 * Backoff is also why the widget must not clear its book on resync — see
 * markStaleForResync in the call sites: the last known book stays on screen (flagged
 * stale) for however long recovery takes.
 */
export const ORDERBOOK_RESYNC_SNAPSHOT_TIMEOUT_MS = 3_000
export const ORDERBOOK_RESYNC_MAX_BACKOFF_MS = 20_000

/** Snapshot wait for the nth resubscribe attempt (1-based): 3s, 6s, 12s, then capped. */
export function resolveResyncBackoffMs(attempt: number): number {
  const exponent = Math.max(0, attempt - 1)
  const delay = ORDERBOOK_RESYNC_SNAPSHOT_TIMEOUT_MS * 2 ** Math.min(exponent, 10)
  return Math.min(delay, ORDERBOOK_RESYNC_MAX_BACKOFF_MS)
}

/**
 * What to do with an incoming delta given how much is already buffered.
 *
 * 'flush-then-buffer' is backpressure, not failure: the buffered deltas are already
 * sequence-validated, so draining them early only changes render cadence. This exists as a
 * named decision because it used to be 'discard the book and resync', which is what emptied
 * the widget during fast markets — the threshold scales with update rate, so volatility
 * guaranteed the wipe. Keeping it a pure function keeps that regression testable.
 */
export type OrderbookDeltaBufferAction = 'buffer' | 'flush-then-buffer'

export function resolveDeltaBufferAction({
  pendingBidCount,
  pendingAskCount,
  incomingBidCount,
  incomingAskCount,
  limit = ORDERBOOK_DELTA_BACKPRESSURE_LIMIT,
}: {
  pendingBidCount: number
  pendingAskCount: number
  incomingBidCount: number
  incomingAskCount: number
  limit?: number
}): OrderbookDeltaBufferAction {
  const overflows =
    pendingBidCount + incomingBidCount > limit || pendingAskCount + incomingAskCount > limit
  return overflows ? 'flush-then-buffer' : 'buffer'
}

export interface OrderbookCursor {
  hasSnapshot: boolean
  lastChangeId: string | null
  lastTimestamp: bigint | null
}

export interface OrderbookTransition {
  kind: 'snapshot' | 'delta'
  orderbook: OrderBook
}

export interface OrderbookBatchResult {
  cursor: OrderbookCursor
  transitions: OrderbookTransition[]
  requiresResync: boolean
  reason: 'gap' | 'out-of-order' | 'snapshot-required' | null
}

export const EMPTY_ORDERBOOK_CURSOR: OrderbookCursor = {
  hasSnapshot: false,
  lastChangeId: null,
  lastTimestamp: null,
}

function parseInt64(value: string | undefined): bigint | null {
  if (!value || value === '0') return null
  try {
    return BigInt(value)
  } catch {
    return null
  }
}

/** Stable chronological ordering for batches whose producer order is not guaranteed. */
export function sortOrderbookBatch(orderbooks: OrderBook[]): OrderBook[] {
  return orderbooks
    .map((orderbook, index) => ({ orderbook, index }))
    .sort((a, b) => {
      const aTimestamp = parseInt64(a.orderbook.timestamp)
      const bTimestamp = parseInt64(b.orderbook.timestamp)
      if (aTimestamp === null || bTimestamp === null) return a.index - b.index
      if (aTimestamp !== bTimestamp) {
        return aTimestamp < bTimestamp ? -1 : 1
      }

      const aChangeId = parseInt64(a.orderbook.changeId)
      const bChangeId = parseInt64(b.orderbook.changeId)
      if (aChangeId !== null && bChangeId !== null && aChangeId !== bChangeId) {
        return aChangeId < bChangeId ? -1 : 1
      }
      return a.index - b.index
    })
    .map(({ orderbook }) => orderbook)
}

export function shouldRefreshCachedOrderbook(
  cursor: OrderbookCursor,
  hasCachedData: boolean
): boolean {
  return hasCachedData && !cursor.hasSnapshot
}

/**
 * Validates one orderbook batch without mutating component state. A delta is never allowed
 * to initialize a book; recovery always requires an explicit snapshot.
 */
export function processOrderbookBatch(
  currentCursor: OrderbookCursor,
  orderbooks: OrderBook[]
): OrderbookBatchResult {
  let cursor = { ...currentCursor }
  const transitions: OrderbookTransition[] = []
  const sorted = sortOrderbookBatch(orderbooks)

  for (const orderbook of sorted) {
    const isSnapshot = orderbook.type === UpdateType.UPDATE_TYPE_SNAPSHOT
    const timestamp = parseInt64(orderbook.timestamp)
    const changeId = parseInt64(orderbook.changeId)
    const prevChangeId = parseInt64(orderbook.prevChangeId)

    if (isSnapshot) {
      // A snapshot supersedes every earlier delta in the same batch.
      transitions.length = 0
      cursor = {
        hasSnapshot: true,
        lastChangeId: changeId === null ? null : orderbook.changeId,
        lastTimestamp: timestamp,
      }
      transitions.push({ kind: 'snapshot', orderbook })
      continue
    }

    if (!cursor.hasSnapshot) {
      // A snapshot later in the same chronologically sorted batch can still recover us.
      continue
    }

    if (changeId !== null && cursor.lastChangeId === orderbook.changeId) continue

    if (timestamp !== null && cursor.lastTimestamp !== null && timestamp < cursor.lastTimestamp) {
      return {
        cursor: { ...EMPTY_ORDERBOOK_CURSOR },
        transitions: [],
        requiresResync: true,
        reason: 'out-of-order',
      }
    }

    if (prevChangeId !== null && cursor.lastChangeId !== null) {
      if (orderbook.prevChangeId !== cursor.lastChangeId) {
        return {
          cursor: { ...EMPTY_ORDERBOOK_CURSOR },
          transitions: [],
          requiresResync: true,
          reason: 'gap',
        }
      }
    }

    cursor = {
      hasSnapshot: true,
      lastChangeId: changeId === null ? null : orderbook.changeId,
      lastTimestamp: timestamp ?? cursor.lastTimestamp,
    }
    transitions.push({ kind: 'delta', orderbook })
  }

  if (!cursor.hasSnapshot) {
    return {
      cursor: { ...EMPTY_ORDERBOOK_CURSOR },
      transitions: [],
      requiresResync: orderbooks.length > 0,
      reason: orderbooks.length > 0 ? 'snapshot-required' : null,
    }
  }

  return { cursor, transitions, requiresResync: false, reason: null }
}

export type OrderbookVisibilityAction = 'none' | 'flush' | 'resync' | 'global-reconnect'

export function resolveOrderbookVisibilityAction({
  hiddenAt,
  visibleAt,
  lastValidMessageAt,
  hasPendingDeltas,
  awaitingSnapshot,
  localStaleThresholdMs,
  globalThresholdMs,
}: {
  hiddenAt: number | null
  visibleAt: number
  lastValidMessageAt: number | null
  hasPendingDeltas: boolean
  awaitingSnapshot: boolean
  localStaleThresholdMs: number
  globalThresholdMs: number
}): OrderbookVisibilityAction {
  if (hiddenAt === null) return 'none'
  const hiddenForMs = Math.max(0, visibleAt - hiddenAt)
  if (hiddenForMs >= globalThresholdMs) return 'global-reconnect'
  if (awaitingSnapshot) return 'resync'

  const receivedWhileHidden =
    lastValidMessageAt !== null && lastValidMessageAt >= hiddenAt && lastValidMessageAt <= visibleAt
  if (!receivedWhileHidden && hiddenForMs >= localStaleThresholdMs) return 'resync'
  if (hasPendingDeltas) return 'flush'
  return 'none'
}

/**
 * User-selectable orderbook refresh rate, matching the backend's market-data cadence:
 * the server sends every 250 ms by default (1000 ms on a data-saver connection — see
 * stream-interval.ts).
 *
 * - `realtime` (default, "250ms"): apply deltas as they arrive. The server already
 *   batches to 250 ms, so a client throttle on top would only add latency; this bypasses
 *   scheduleKeyedFlush entirely — no coalescing or timer overhead.
 * - `slow` ("1000ms"): coalesce onto one flush a second for this book, for a calmer
 *   ladder. Client-side, because the server cadence is per connection, not per book.
 */
export type OrderbookRefreshRate = 'realtime' | 'slow'

export const ORDERBOOK_REFRESH_RATES: readonly OrderbookRefreshRate[] = ['realtime', 'slow']

/** Flush interval of the throttled refresh rate (`realtime` has none — see above). */
export const ORDERBOOK_SLOW_REFRESH_INTERVAL_MS = 1000

/**
 * Resolves a refresh-rate selection to the ms interval a scheduleKeyedFlush call
 * should use, or `null` when the caller should skip scheduleKeyedFlush entirely and
 * flush immediately instead (`realtime`). Shared by every scheduleKeyedFlush call site
 * so what each rate means lives in one place.
 */
export function resolveOrderbookFlushIntervalMs(refreshRate: OrderbookRefreshRate): number | null {
  return refreshRate === 'slow' ? ORDERBOOK_SLOW_REFRESH_INTERVAL_MS : null
}

type FlushCallback = () => void

const pendingFlushCallbacksByKey = new Map<string, Set<FlushCallback>>()
const flushTimersByKey = new Map<string, ReturnType<typeof setTimeout>>()

/**
 * Trailing-edge throttle shared by `${exchange}:${symbol}` key. The orderbook widget
 * and the Quick Order popup can both be mounted for the same symbol and both buffer
 * their own delta updates into the same bid/ask atoms — sharing one timer per key here
 * means they coalesce onto a single ORDERBOOK_DELTA_FLUSH_INTERVAL_MS flush instead of
 * each running an independent, competing timer for the same data.
 */
export function scheduleKeyedFlush(
  key: string,
  callback: FlushCallback,
  intervalMs: number = ORDERBOOK_DELTA_FLUSH_INTERVAL_MS
): void {
  let callbacks = pendingFlushCallbacksByKey.get(key)
  if (!callbacks) {
    callbacks = new Set()
    pendingFlushCallbacksByKey.set(key, callbacks)
  }
  callbacks.add(callback)

  if (flushTimersByKey.has(key)) return
  const timer = setTimeout(() => {
    flushTimersByKey.delete(key)
    const scheduled = pendingFlushCallbacksByKey.get(key)
    pendingFlushCallbacksByKey.delete(key)
    scheduled?.forEach((cb) => cb())
  }, intervalMs)
  flushTimersByKey.set(key, timer)
}

/** Drop a callback registered via scheduleKeyedFlush, e.g. on symbol change or unmount. */
export function cancelKeyedFlush(key: string, callback: FlushCallback): void {
  pendingFlushCallbacksByKey.get(key)?.delete(callback)
}

export function toNumEntry(e: BookEntry): NumEntry {
  return { ...e, _p: parseFloat(e.price), _q: parseFloat(e.quantity) } as NumEntry
}

export function formatPrice(price: string | number, precision?: number): string {
  const num = typeof price === 'string' ? parseFloat(price) : price

  if (precision !== undefined) {
    return num.toLocaleString('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    })
  }

  // For very small numbers, ensure we show significant digits
  if (num < 0.001 && num > 0) {
    return num.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })
  }

  return num.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 4 })
}

export function parsePositiveNumber(value: string | number | undefined | null): number | null {
  if (value == null || value === '') return null
  const num = typeof value === 'number' ? value : parseFloat(value)
  return !isNaN(num) && num > 0 ? num : null
}

export function resolveQuoteConversionPrice(
  isSelfQuoted: boolean,
  ...candidates: (string | number | undefined | null)[]
): number {
  if (!isSelfQuoted) return 1

  for (const candidate of candidates) {
    const price = parsePositiveNumber(candidate)
    if (price !== null) return price
  }

  return 0
}

/** Convert a base amount of a self-quoted option premium into USD. */
export function calculateOptionUsdAmount(
  amount: string | number | undefined | null,
  optionPrice: string | number | undefined | null,
  underlyingPrice: string | number | undefined | null
): number {
  const parsedAmount = parsePositiveNumber(amount)
  const parsedOptionPrice = parsePositiveNumber(optionPrice)
  const parsedUnderlyingPrice = parsePositiveNumber(underlyingPrice)

  if (parsedAmount === null || parsedOptionPrice === null || parsedUnderlyingPrice === null) {
    return 0
  }

  return parsedAmount * parsedOptionPrice * parsedUnderlyingPrice
}

export function getDecimalsFromIncrement(increment: number): number {
  if (!Number.isFinite(increment) || increment <= 0) return 0

  const normalized = increment < 1e-6 ? increment.toFixed(12) : increment.toString()
  const plain = normalized.replace(/e.*/, '').replace(/0+$/, '')
  const dotIdx = plain.indexOf('.')
  return dotIdx === -1 ? 0 : plain.length - dotIdx - 1
}

export function resolveTickSize(
  globalInstrument: Instrument | null,
  selectedInstrument: { tickSize?: string; pricePrecision?: string } | undefined,
  selectedItem: { pricePrecision?: string } | undefined,
  referencePrice: number | null
): number | null {
  const baseTickSize = parsePositiveNumber(
    globalInstrument?.tickSize ?? selectedInstrument?.tickSize
  )

  if (baseTickSize) {
    let tickSize = baseTickSize
    const steps = globalInstrument?.tickSizeSteps ?? []

    if (referencePrice != null && steps.length > 0) {
      const sortedSteps = [...steps].sort((a, b) => Number(a.abovePrice) - Number(b.abovePrice))
      for (const step of sortedSteps) {
        const abovePrice = Number(step.abovePrice)
        const stepTickSize = parsePositiveNumber(step.tickSize)
        if (stepTickSize && referencePrice > abovePrice) {
          tickSize = stepTickSize
        }
      }
    }

    return tickSize
  }

  const pricePrecision =
    globalInstrument?.pricePrecision ??
    selectedInstrument?.pricePrecision ??
    selectedItem?.pricePrecision
  // pricePrecision is a literal price step increment (mirrors tickSize), not a
  // decimal-place count — every venue's backend producer emits it that way.
  const precision = pricePrecision == null ? null : parseFloat(pricePrecision)
  if (precision == null || isNaN(precision) || precision <= 0) return null

  return precision
}

/**
 * Decides whether the orderbook's user-facing aggregation level should reset for `symbol`,
 * and whether this call is authoritative enough to mark that symbol as initialized.
 *
 * Aggregation levels can be computed from two sources when a symbol changes: a real WS
 * snapshot (authoritative — has the new instrument's actual tick size), or a provisional
 * guess derived from whatever stale/cached book a newly-attached shared atom already held
 * before that snapshot arrives (see orderbook.tsx's cached-data effect). Only the
 * authoritative source may record `lastResetSymbol`, so a provisional guess computed from
 * the PREVIOUS instrument's leftover book can never pre-empt the real reset that follows it
 * — that race used to leave the aggregation stuck on the old instrument's tick size (e.g. an
 * option showing 0.0005 instead of its real 0.0001 minimum right after switching from
 * BTC-PERPETUAL).
 */
export function resolveAggregationReset(params: {
  symbol: string
  lastResetSymbol: string | null
  levels: number[]
  currentAggregation: number
  isAuthoritative: boolean
}): { nextAggregation: number | null; nextLastResetSymbol: string | null } {
  const { symbol, lastResetSymbol, levels, currentAggregation, isAuthoritative } = params
  if (levels.length === 0) {
    return { nextAggregation: null, nextLastResetSymbol: lastResetSymbol }
  }

  if (lastResetSymbol !== symbol) {
    return {
      nextAggregation: levels[0],
      nextLastResetSymbol: isAuthoritative ? symbol : lastResetSymbol,
    }
  }

  if (!levels.includes(currentAggregation)) {
    return { nextAggregation: levels[0], nextLastResetSymbol: lastResetSymbol }
  }

  return { nextAggregation: null, nextLastResetSymbol: lastResetSymbol }
}

/**
 * Effective price display precision (decimal places), accounting for Hyperliquid-style
 * sig-fig tick ladders (tickSizeSteps) — the same resolveTickSize + getDecimalsFromIncrement
 * composition already used by the orderbook and order form. Any surface that formats a
 * venue price (watchlist bid/ask, trade tape, orderbook, order form) should go through this
 * instead of reading `tickSize`/`pricePrecision` directly, so they can't drift out of sync
 * with each other the way recent-trades/watchlist did before this existed.
 */
export function getEffectivePricePrecision(
  instrument:
    | { tickSize?: string; tickSizeSteps?: Instrument['tickSizeSteps']; pricePrecision?: string }
    | null
    | undefined,
  referencePrice: number | null
): number | null {
  const tickSize = resolveTickSize(instrument as Instrument, undefined, undefined, referencePrice)
  if (tickSize == null) return null
  return getDecimalsFromIncrement(tickSize)
}

export function getOrderbookReferencePrice(
  bids: Iterable<NumEntry>,
  asks: Iterable<NumEntry>,
  fallback: number | null = null
): number | null {
  let bestBid = -Infinity
  for (const bid of bids) if (bid._p > bestBid) bestBid = bid._p

  let bestAsk = Infinity
  for (const ask of asks) if (ask._p < bestAsk) bestAsk = ask._p

  if (bestBid > -Infinity && bestAsk < Infinity) return (bestBid + bestAsk) / 2
  if (bestBid > -Infinity) return bestBid
  if (bestAsk < Infinity) return bestAsk
  return fallback
}

/**
 * Merge orderbook entries — stores pre-parsed NumEntry to avoid parseFloat on render
 */
export function mergeOrderbookEntries(
  prev: Map<string, NumEntry>,
  updates: BookEntry[]
): Map<string, NumEntry> {
  if (updates.length === 0) return prev

  const next = new Map(prev)

  for (const entry of updates) {
    const { price, quantity, action = BookAction.BOOK_ACTION_NEW } = entry

    switch (action) {
      case BookAction.BOOK_ACTION_NEW:
      case BookAction.BOOK_ACTION_CHANGE:
        // Quantity '0' or empty is the signal to remove a price level in many protocols.
        if (quantity === '0' || quantity === '') {
          next.delete(price)
        } else {
          const existing = next.get(price)
          if (action === BookAction.BOOK_ACTION_CHANGE && existing) {
            // Optimization: retain existing parsed price when only the quantity changed.
            next.set(price, {
              ...entry,
              _p: existing._p,
              _q: parseFloat(quantity),
            })
          } else {
            next.set(price, toNumEntry(entry))
          }
        }
        break

      case BookAction.BOOK_ACTION_DELETE:
        next.delete(price)
        break

      default:
        // Resilience: fallback for UNSPECIFIED or UNRECOGNIZED actions.
        if (!quantity || quantity === '0') {
          next.delete(price)
        } else {
          next.set(price, toNumEntry(entry))
        }
        break
    }
  }

  return next
}

/**
 * Whether a market-data subscription for `symbol` is worth opening.
 *
 * A watchlist entry can carry a bare settlement currency — "KRW" on Upbit,
 * "USDT" on Bybit — because the instrument picker reads the master catalog,
 * which is wider than what a venue actually streams. Subscribing to one costs
 * public-api three 3s snapshot RPC attempts per request and never yields a
 * tick; on prod one such symbol was 85% of all snapshot timeouts.
 *
 * Only a *loaded* list that lacks the symbol is evidence of absence: while it
 * is pending we hold the subscription rather than open a doomed one, and an
 * errored list proves nothing so the old unconditional behaviour stands.
 */
export function shouldStreamSymbol(
  symbol: string | null,
  listedSymbols: { has: (symbol: string) => boolean },
  instrumentListStatus: 'pending' | 'error' | 'success'
): boolean {
  if (symbol === null) return false
  if (instrumentListStatus === 'error') return true
  return instrumentListStatus === 'success' && listedSymbols.has(symbol)
}
